// components/session/SessionBooking/index.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, isWeekend, addDays } from 'date-fns';
import { toast } from 'sonner';
import { 
    fetchAvailableSlots,
    requestSession,
    selectAvailableSlots,
    selectSessionsLoading,
    setSelectedDate
} from '../../../store/slices/sessionSlice';
import { Button } from '../../../styles/components';
import {
    BookingWrapper,
    BookingHeader,
    BookingGrid,
    CalendarSection,
    TimeSlotSection,
    TimeSlot,
    BookingSummary,
    FormGroup,
    Label,
    TextArea
} from './styles';

const SessionBooking = () => {
    const dispatch = useDispatch();
    const availableSlots = useSelector(selectAvailableSlots);
    const loading = useSelector(selectSessionsLoading);
    
    const selectedDate = useSelector(state => state.sessions.selectedDate);
    const [selectedTime, setSelectedTime] = useState(null);
    const [notes, setNotes] = useState('');
    const [duration, setDuration] = useState(1); // Default 1 hour

    const handleDateSelect = (date) => {
        dispatch(setSelectedDate(date));
        dispatch(fetchAvailableSlots(date));
    };

    const handleTimeSelect = (time) => {
        setSelectedTime(time);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedDate || !selectedTime) {
            toast.error('Please select both date and time');
            return;
        }

        try {
            await dispatch(requestSession({
                date: selectedDate,
                timeSlot: selectedTime,
                duration,
                notes
            })).unwrap();

            toast.success('Session request submitted successfully!');
            // Reset form
            setSelectedDate(null);
            setSelectedTime(null);
            setNotes('');
            setDuration(1);
        } catch (error) {
            toast.error(error.message || 'Failed to request session');
        }
    };

    return (
        <BookingWrapper>
            <BookingHeader>
                <h2>Book a Photography Session</h2>
                <p>Select your preferred date and time</p>
            </BookingHeader>

            <form onSubmit={handleSubmit}>
                <BookingGrid>
                    <CalendarSection>
                        <h3>Select Date</h3>
                        <p>Only weekend slots available</p>
                        <DatePicker
                            selected={selectedDate}
                            onChange={handleDateSelect}
                            minDate={new Date()}
                            maxDate={addDays(new Date(), 30)}
                            filterDate={isWeekend}
                            inline
                            strictParsing
                        />
                    </CalendarSection>

                    <TimeSlotSection>
                        <h3>Select Time</h3>
                        {selectedDate ? (
                            loading ? (
                                <p>Loading available slots...</p>
                            ) : availableSlots.length > 0 ? (
                                <div className="time-slots">
                                    {availableSlots.map((slot) => (
                                        <TimeSlot
                                            key={slot}
                                            type="button"
                                            selected={selectedTime === slot}
                                            onClick={() => handleTimeSelect(slot)}
                                        >
                                            {slot}
                                        </TimeSlot>
                                    ))}
                                </div>
                            ) : (
                                <p>No slots available for selected date</p>
                            )
                        ) : (
                            <p>Please select a date first</p>
                        )}
                    </TimeSlotSection>

                    {(selectedDate && selectedTime) && (
                        <BookingSummary>
                            <h3>Session Details</h3>
                            <FormGroup>
                                <Label>Date</Label>
                                <p>{format(selectedDate, 'PPPP')}</p>
                            </FormGroup>
                            <FormGroup>
                                <Label>Time</Label>
                                <p>{selectedTime}</p>
                            </FormGroup>
                            <FormGroup>
                                <Label>Duration (hours)</Label>
                                <select
                                    value={duration}
                                    onChange={(e) => setDuration(Number(e.target.value))}
                                >
                                    <option value={1}>1 hour</option>
                                    <option value={2}>2 hours</option>
                                </select>
                            </FormGroup>
                            <FormGroup>
                                <Label>Special Notes</Label>
                                <TextArea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Any special requests or notes..."
                                    rows={4}
                                />
                            </FormGroup>
                            <Button type="submit" disabled={loading}>
                                Request Session
                            </Button>
                        </BookingSummary>
                    )}
                </BookingGrid>
            </form>
        </BookingWrapper>
    );
};

export default SessionBooking;